import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { CONTACT_FORM } from "@/constants/testIds";
import { CONTACT } from "@/constants/site";
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", company: "", email: "", phone: "", interest: "", message: "" };

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "error", msg: t.contact.form.required });
      return;
    }
    setStatus({ state: "sending", msg: "" });
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      setStatus({ state: "success", msg: t.contact.form.success });
      setForm(initial);
    } catch (err) {
      setStatus({ state: "error", msg: t.contact.form.error });
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#0A0A0A] border-t border-white/5">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* LEFT: Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <div className="section-label mb-6">{t.contact.label}</div>
          <h2 className="font-heading uppercase text-white text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-neutral-400 text-base leading-relaxed max-w-md">
            {t.contact.body}
          </p>

          <div className="mt-10 space-y-6">
            <a href={CONTACT.phoneHref} className="flex items-start gap-4 group">
              <div className="w-11 h-11 border border-white/10 flex items-center justify-center group-hover:border-[#F5B800] transition-colors">
                <Phone size={16} className="text-[#F5B800]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">Phone</div>
                <div className="text-white text-lg font-semibold group-hover:text-[#F5B800] transition-colors">{CONTACT.phone}</div>
              </div>
            </a>

            <a href={CONTACT.emailHref} className="flex items-start gap-4 group">
              <div className="w-11 h-11 border border-white/10 flex items-center justify-center group-hover:border-[#F5B800] transition-colors">
                <Mail size={16} className="text-[#F5B800]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">Email</div>
                <div className="text-white text-lg font-semibold group-hover:text-[#F5B800] transition-colors">{CONTACT.email}</div>
              </div>
            </a>

            <div className="flex items-start gap-4">
              <div className="w-11 h-11 border border-white/10 flex items-center justify-center">
                <MapPin size={16} className="text-[#F5B800]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">Address</div>
                <div className="text-white text-base max-w-xs leading-snug">{t.contact.address}</div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="mt-10 border border-white/10 h-56 overflow-hidden">
            <iframe
              title="Trom Motors location"
              src={CONTACT.mapEmbed}
              width="100%"
              height="100%"
              loading="lazy"
              className="map-dark border-0"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </motion.div>

        {/* RIGHT: Form */}
        <motion.form
          data-testid={CONTACT_FORM.root}
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 bg-[#141414] border border-white/10 p-8 md:p-12 space-y-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <input
              data-testid={CONTACT_FORM.name}
              className="tm-input"
              placeholder={t.contact.form.name}
              value={form.name}
              onChange={update("name")}
            />
            <input
              data-testid={CONTACT_FORM.company}
              className="tm-input"
              placeholder={t.contact.form.company}
              value={form.company}
              onChange={update("company")}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <input
              data-testid={CONTACT_FORM.email}
              type="email"
              className="tm-input"
              placeholder={t.contact.form.email}
              value={form.email}
              onChange={update("email")}
            />
            <input
              data-testid={CONTACT_FORM.phone}
              className="tm-input"
              placeholder={t.contact.form.phone}
              value={form.phone}
              onChange={update("phone")}
            />
          </div>
          <input
            data-testid={CONTACT_FORM.interest}
            className="tm-input"
            placeholder={t.contact.form.interest}
            value={form.interest}
            onChange={update("interest")}
          />
          <textarea
            data-testid={CONTACT_FORM.message}
            className="tm-input min-h-[160px] resize-none"
            placeholder={t.contact.form.message}
            value={form.message}
            onChange={update("message")}
          />

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
            <button
              type="submit"
              data-testid={CONTACT_FORM.submit}
              disabled={status.state === "sending"}
              className="btn-primary sharp disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status.state === "sending" ? t.contact.form.sending : t.contact.form.submit}
              <Send size={15} strokeWidth={2.5} />
            </button>

            {status.state === "success" && (
              <div data-testid={CONTACT_FORM.success} className="flex items-center gap-2 text-[#F5B800] text-sm">
                <CheckCircle2 size={16} />
                {status.msg}
              </div>
            )}
            {status.state === "error" && (
              <div data-testid={CONTACT_FORM.error} className="flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle size={16} />
                {status.msg}
              </div>
            )}
          </div>
        </motion.form>
      </div>
    </section>
  );
}
